import React, { useEffect, useState } from "react";
import { Box, Button, Typography, Card, CardMedia, CardContent, IconButton, Avatar, Divider, Grid, Container, useTheme, Modal } from "@mui/material";
import useResponsive from "../hooks/useResponsive";
import { getRelatedBooks, getBookById, addtoCart, getComboOfferById } from "../services/Apis";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import YouTube from 'react-youtube';
import { useFilter } from "../context/FilterContext";

const BookOverviewResults = ({ type }) => {
    // Function to extract YouTube video ID from URL
    const getYouTubeId = (url) => {
        if (!url) return null;
        
        // Handle different YouTube URL formats
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        
        return (match && match[2].length === 11) ? match[2] : null;
    };
    
    
   
   
    const [videoModalOpen, setVideoModalOpen] = useState(false);

    const handleOpenVideo = () => setVideoModalOpen(true);
    const handleCloseVideo = () => setVideoModalOpen(false);
    const location = useLocation();
    const fromCombo = location.state?.fromCombo === true;

    const [apiBook, setApiBook] = useState(null);
    const [relatedBooks, setRelatedBooks] = useState([]);
    const [mainImageIndex, setMainImageIndex] = useState(0);
    const theme = useTheme();
    const { isSmallScreen } = useResponsive();
    const navigate = useNavigate();
    const { token, isLoggedIn } = useFilter();
    const { id } = useParams();

    const handleBuyNow = async (book) => {
        if (isLoggedIn && token) {
            try {
                navigate('/checkout', { state: book });
            } catch (error) {
                console.log("Failed to add to cart. Please login or try again.");
            }
        } else {
            navigate("/login")
        }
    };

    const handleAddToCart = async ({ bookId, comboId }) => {
        if (isLoggedIn && token) {
            try {
                if (bookId) {
                    await addtoCart({ bookId, quantity: 1 });
                } else if (comboId) {
                    await addtoCart({ comboId, quantity: 1 });
                }
                navigate('/cart');
            } catch (error) {
                alert("Failed to add to cart. Please login or try again.");
            }
        } else {
            navigate("/login")
        }
    };


    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            if (type === "combo") {
                // Fetch specific combo data using the new API
                const combo = await getComboOfferById(id);
                setApiBook(combo || null);
                setMainImageIndex(0);
                setRelatedBooks([]); // You can fetch related combos if needed
            } else {
                // Fetch book data
                const mainBook = await getBookById(id);
                setApiBook(mainBook);
                setMainImageIndex(0);
                if (mainBook) {
                    const relBooks = await getRelatedBooks(mainBook.id, mainBook.subCategoryId);
                    setRelatedBooks(relBooks);
                }
            }
        };
        fetchData();
    }, [id, type]);

    if (!apiBook) {
        return <Typography>Loading...</Typography>;
    }

    // If viewing book from combo, hide related books and purchase buttons
    const hidePurchaseAndRelated = fromCombo && type !== "combo";

    // --- RENDER FOR COMBO ---
    if (type === "combo") {
        return (
            <Container maxWidth="xl">
                <Box mt={4}>
                    <Box display="flex" gap={4} flexDirection={isSmallScreen ? "column" : "row"}>
                        {/* Combo Image */}
                        <Box
                            minWidth={isSmallScreen ? "100%" : 430}
                            maxWidth={isSmallScreen ? "100%" : 430}
                            width={isSmallScreen ? "100%" : 400}

                            sx={{
                                gap: 2, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                                background: "#fff", position: isSmallScreen ? "static" : "sticky",
                                top: isSmallScreen ? undefined : 20,
                                height: isSmallScreen ? "auto" : "fit-content",
                                mb: isSmallScreen ? 2 : 0,
                            }}
                        >
                            <img
                                loading="lazy"
                                src={apiBook.imageUrl}
                                alt={apiBook.title}
                                style={{
                                    width: '100%',
                                    maxHeight: 420,
                                    borderRadius: '8px',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        </Box>

                        {/* Combo Details */}
                        <Box flex={1} p={isSmallScreen ? 0 : '0px 16px'}
                            sx={{
                                display: "flex", flexDirection: "column", justifyContent: "stretch",
                                maxHeight: isSmallScreen ? "none" : "100vh", overflowY: isSmallScreen ? "visible" : "auto",
                                scrollbarWidth: "none", msOverflowStyle: "none",
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                            }}
                        >
                            <Typography variant="h4" sx={{ color: "#222", fontWeight: 600, mb: 1 }}>
                                {apiBook.title}
                            </Typography>
                            <Typography variant="h5" sx={{ color: "#888", mb: 1 }}>
                                {apiBook.description}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2} mt={1} mb={3}>
                                <Typography variant="h4" sx={{ color: "#222", fontWeight: 700 }}>
                                    ₹{apiBook.price}
                                </Typography>
                                {apiBook.discountOffer > 0 && (
                                    <Typography variant="h5" sx={{ textDecoration: 'line-through', color: "#888" }}>
                                        ₹{(apiBook.price / (1 - apiBook.discountOffer / 100)).toFixed(2)}
                                    </Typography>
                                )}
                            </Box>
                            <Typography variant="h5" sx={{ color: "#388e3c", fontWeight: 600, mb: 2 }}>
                                Combo Offer
                            </Typography>
                            <Box>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: isSmallScreen ? "stretch" : "center", flexDirection: isSmallScreen ? "column" : "row" }}  >
                                    <Button 
                                        onClick={() => navigate('/checkout', { state: { ...apiBook, isCombo: true } })} 
                                        variant="contained" 
                                        color="primary" 
                                        disableRipple 
                                        disableFocusRipple
                                        disabled={apiBook.comboOfferBooks?.some(item => !item.book.availability)}
                                        sx={{
                                            minWidth: 140, 
                                            fontWeight: 600, 
                                            boxShadow: 'none', 
                                            width: isSmallScreen ? "100%" : "auto",
                                            '&:hover': { 
                                                boxShadow: 'none', 
                                                backgroundColor: 'primary.main' 
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: 'primary.main',
                                                color: '#fff',
                                                opacity: 0.7,
                                            },
                                        }}
                                    >
                                        Buy Now
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        disableRipple
                                        disableFocusRipple
                                        disabled={apiBook.comboOfferBooks?.some(item => !item.book.availability)}
                                        onClick={() => handleAddToCart({ comboId: apiBook.id })}
                                        sx={{
                                            minWidth: 140,
                                            fontWeight: 600,
                                            boxShadow: 'none',
                                            width: isSmallScreen ? "100%" : "auto",
                                            '&.Mui-disabled': {
                                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                                color: 'rgba(0, 0, 0, 0.26)',
                                            },
                                        }}
                                    >
                                        Add to Cart
                                    </Button>
                                </Box>
                                {apiBook.comboOfferBooks?.some(item => !item.book.availability) && (
                                    <Typography 
                                        variant="body2" 
                                        color="error" 
                                        sx={{ 
                                            textAlign: 'left',
                                            mt: 2,
                                            fontWeight: 500,
                                             // Align with buttons
                                            [theme.breakpoints.down('sm')]: {
                                                pl: 0,
                                                textAlign: 'center',
                                            },
                                        }}
                                    >
                                        Note: One or more books in this combo are out of stock
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ mt: 4, mb: 1 }}>
                                <Divider />
                            </Box>
                            {/* List books in combo */}
                            <Box mt={2}>
                                <Typography variant="h4" fontWeight="bold" gutterBottom>Books in this Combo:</Typography>
                                <Grid container spacing={2}>
                                    {apiBook.comboOfferBooks?.map((item, idx) => (
                                        <Grid item sx={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={idx}>
                                            <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 220 , maxHeight: 220 ,minWidth: 220, maxWidth: 220, position: 'relative' }}>
    {!item.book.availability && (
    <Box
        sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-20deg)',
            zIndex: 10,
            width: '200px',
            minHeight: '48px',
            bgcolor: 'rgba(120,120,120,0.85)',
            color: '#fff',
            textAlign: 'center',
            fontWeight: 900,
            fontSize: 26,
            py: 2,
            px: 0,
            borderRadius: 3,
            letterSpacing: 2,
            userSelect: 'none',
            boxShadow: 4,
            opacity: 0.92,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            '&:hover': {
                opacity: 1,
            },
        }}
        onClick={() => navigate(`/book-overview/${item.book.id}`, { state: { fromCombo: true } })}
    >
        OUT OF STOCK
    </Box>
)}
                                                {item.book?.bookImages?.[0]?.imageUrl ? (
                                                    <img
                                                        src={item.book.bookImages[0].imageUrl}
                                                        onClick={() => navigate(`/book-overview/${item.book.id}`, { state: { fromCombo: true } })}
                                                        alt={item.book.title}
                                                        style={{
                                                            width: 80,
                                                            height: 110,
                                                            cursor: 'pointer',
                                                            objectFit: 'cover',
                                                            marginBottom: 8,
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: 80,
                                                            height: 110,
                                                            borderRadius: 2,
                                                            marginBottom: 1,
                                                            background: "#f5f5f5",
                                                        }}
                                                    />
                                                )}

                                                <Typography variant="h6" fontWeight={600} sx={{maxWidth: 180 , textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap'}} align="center">{item.book.title}</Typography>
                                                <Typography variant="body2" color="text.secondary">{item.book.language} | {item.book.format}</Typography>
                                                <Typography variant="body2" color="text.secondary">Year: {item.book.publishingYear}</Typography>
                                                <Typography variant="body2" color="text.secondary">Code: {item.book.code}</Typography>
                                                <Button
                                                    size="small"
                                                    sx={{ mt: 1, textTransform: 'none', fontSize: 12 }}
                                                    disabled={!item.book.availability}
                                                    onClick={() => navigate(`/book-overview/${item.book.id}`, { state: { fromCombo: true } })}
                                                >
                                                    View Book
                                                </Button>
                                            </Card>
                                        </Grid>
// ...
                                    ))}
                                </Grid>
                            </Box>
                        </Box>
                    </Box>
                    
                   
                </Box>
            </Container>
        );
    }

    // --- RENDER FOR BOOK (default) ---
    return (
        <>
            <Container maxWidth="xl" >
                <Box mt={4} >

                    <Box
                        display="flex" gap={4}
                        flexDirection={isSmallScreen ? "column" : "row"}
                    >
                        {/* Book Image */}
                        <Box
                            minWidth={isSmallScreen ? "100%" : 430}
                            maxWidth={isSmallScreen ? "100%" : 430}
                            width={isSmallScreen ? "100%" : 400}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                background: "#fff",
                                position: isSmallScreen ? "static" : "sticky",
                                top: isSmallScreen ? undefined : 20,
                                height: isSmallScreen ? "auto" : "fit-content",
                                mb: isSmallScreen ? 2 : 0,
                            }}
                        >
                            {/* Main image */}
                            <img
                                loading="lazy"
                                src={apiBook?.bookImages?.[mainImageIndex]?.imageUrl}
                                alt={apiBook.title}
                                style={{
                                    width: '100%',
                                    maxHeight: 420,
                                    borderRadius: '8px',
                                    objectFit: 'contain',
                                    display: 'block',
                                    marginBottom: '16px',
                                }}
                            />
                            
                            {/* Thumbnail images - displayed horizontally below the main image (max 4) */}
                            {Array.isArray(apiBook?.bookImages) && apiBook.bookImages.length > 1 && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'row',
                                    gap: 1,
                                    justifyContent: 'center',
                                    width: '100%',
                                    padding: '0 10px'
                                }}>
                                    {apiBook.bookImages.slice(0, 4).map((img, idx) =>
                                        img?.imageUrl ? (
                                            <Box 
                                                key={idx}
                                                sx={{
                                                    width: '60px',
                                                    height: '60px',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    border: mainImageIndex === idx ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                    opacity: mainImageIndex === idx ? 1 : 0.8,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        opacity: 1,
                                                        transform: 'scale(1.05)'
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={img.imageUrl}
                                                    style={{
                                                        cursor: 'pointer',
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        display: 'block'
                                                    }}
                                                    loading="lazy"
                                                    alt={`${apiBook.title} - ${idx + 1}`}
                                                    onClick={() => setMainImageIndex(idx)}
                                                />
                                            </Box>
                                        ) : null
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* Book Details section */}
                        <Box flex={1} p={isSmallScreen ? 0 : '0px 16px'}
                            sx={{
                                display: "flex", flexDirection: "column", justifyContent: "stretch",
                                maxHeight: isSmallScreen ? "none" : "100vh", overflowY: isSmallScreen ? "visible" : "auto",
                                scrollbarWidth: "none", msOverflowStyle: "none",
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                            }}
                        >
                            {/* Title */}
                            <Typography variant="h4" sx={{ color: "#222", fontWeight: 600, mb: 1 }}>
                                {apiBook.title}
                            </Typography>

                            {/* Edition and Format subtitle */}
                            <Typography variant="h5" sx={{ color: "#888", mb: 1 }}>
                                {apiBook.edition}
                            </Typography>
                             {console.log(apiBook)}
                            {/* Author and Format */}
                            <Box display="flex" alignItems="center" gap={2} mb={1}>
                                <Typography variant="h5">
                                    by <b>{apiBook.author?.name || 'Unknown Author'}</b> (Author)
                                </Typography>
                                {apiBook.author?.imageUrl && (
                                    <Avatar
                                        alt={apiBook.author.name}
                                        src={apiBook.author.imageUrl}
                                        sx={{ width: 40, height: 40 }}
                                    />
                                )}
                            </Box>

                            {/* Ratings */}
                           

                            {/* Price */}
                            <Box display="flex" alignItems="center" gap={2} mt={1} mb={3}>
                                <Typography variant="h4" sx={{ color: "#222", fontWeight: 700 }}>
                                    ₹{apiBook.price}
                                </Typography>
                                {apiBook.discountOffer > 0 && (
                                    <Typography variant="h5" sx={{ textDecoration: 'line-through', color: "#888" }}>
                                        ₹{(apiBook.price / (1 - apiBook.discountOffer / 100)).toFixed(2)}
                                    </Typography>
                                )}
                            </Box>

                            {/* Availability */}
                            <Typography variant="h4" sx={{ color: apiBook.availability ? "#388e3c" : "#888", fontWeight: 600, mb: 2 }}>
                                Availability: <span style={{ color: apiBook.availability ? "#388e3c" : "#dc0000" }}>{apiBook.availability ? "In Stock" : "Out of Stock"}</span>
                            </Typography>

                            {/* Details Row */}
                            <Grid container spacing={isSmallScreen ? 2 : 4}
                                mb={2}
                            >
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="h5" sx={{ color: "#888", fontWeight: 500, mb: 0.5 }}>Code:</Typography>
                                    <Typography variant="h4" fontWeight={600}>{apiBook.code}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="h5" sx={{ color: "#888", fontWeight: 500, mb: 0.5 }}>Language:</Typography>
                                    <Typography variant="h4" fontWeight={600}>{apiBook.language}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="h5" sx={{ color: "#888", fontWeight: 500, mb: 0.5 }}>Format:</Typography>
                                    <Typography variant="h4" fontWeight={600}>{apiBook.format}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="h5" sx={{ color: "#888", fontWeight: 500, mb: 0.5 }}>ISBN:</Typography>
                                    <Typography variant="h4" fontWeight={600}>{apiBook.ISBN}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="h5" sx={{ color: "#888", fontWeight: 500, mb: 0.5 }}>Publishing Year:</Typography>
                                    <Typography variant="h4" fontWeight={600}>{apiBook.publishingYear}</Typography>
                                </Grid>
                            </Grid>
                            <Typography variant="h4" sx={{mb:2 }}  gutterBottom><span style={{color:"red"}}>*Note : </span>Kindly note that this product is not eligible for exchange or return.</Typography>
                            {/* Buttons - DESIGN UNCHANGED */}
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: isSmallScreen ? 'stretch' : 'center', 
                                flexDirection: isSmallScreen ? 'column' : 'row', 
                                width: '100%',
                                flexWrap: 'nowrap',
                                overflow: 'hidden'
                            }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 2, 
                                    flexDirection: isSmallScreen ? 'column' : 'row', 
                                    flex: 1,
                                    overflow: 'hidden',
                                    flexWrap: 'wrap'
                                }}>
                                    {!hidePurchaseAndRelated && (apiBook.availability ? (
                                        <>
                                            <Button
                                                onClick={() => handleBuyNow(apiBook)}
                                                variant="contained"
                                                color="primary"
                                                disableRipple
                                                disableFocusRipple
                                                sx={{
                                                    minWidth: 140,
                                                    fontWeight: 600,
                                                    boxShadow: 'none',
                                                    flex: isSmallScreen ? '1 1 100%' : '0 0 auto',
                                                    '&:hover': { boxShadow: 'none', backgroundColor: 'primary.main' },
                                                }}
                                            >
                                                Buy Now
                                            </Button>
                                            <Box sx={{ display: 'flex', gap: 2, flex: isSmallScreen ? '1 1 100%' : '0 0 auto', flexDirection: isSmallScreen ? 'column' : 'row' }}>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    disableRipple
                                                    disableFocusRipple
                                                    onClick={() => handleAddToCart({ bookId: apiBook.id })}
                                                    sx={{
                                                        minWidth: 140,
                                                        fontWeight: 600,
                                                        boxShadow: 'none',
                                                        flex: '1 1 auto',
                                                        '&:hover': { boxShadow: 'none' },
                                                    }}
                                                >
                                                    Add to Cart
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={(e) => {
                                                        try {
                                                            e.preventDefault();
                                                            console.log('PDF Button Clicked');
                                                            console.log('Original PDF URL:', apiBook.previewUrl);
                                                            
                                                            const pdfUrl = apiBook.previewUrl || '/sample.pdf';
                                                            console.log('Using PDF URL:', pdfUrl);
                                                            
                                                            // Ensure the URL is absolute
                                                            const absoluteUrl = pdfUrl.startsWith('http') 
                                                                ? pdfUrl 
                                                                : `${window.location.origin}${pdfUrl.startsWith('/') ? '' : '/'}${pdfUrl}`;
                                                            
                                                            console.log('Opening PDF URL:', absoluteUrl);
                                                            const newWindow = window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                                                            
                                                            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                                                                console.error('Failed to open PDF: Popup was blocked');
                                                                alert('Please allow popups for this site to view the PDF');
                                                            }
                                                        } catch (error) {
                                                            console.error('Error opening PDF:', error);
                                                            alert('Failed to open PDF. Please try again.');
                                                        }
                                                    }}
                                                    sx={{
                                                        minWidth: 140,
                                                        fontWeight: 600,
                                                        boxShadow: 'none',
                                                        flex: '1 1 auto',
                                                        borderColor: '#CB4422',
                                                        color: '#CB4422',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                            borderColor: '#CB4422',
                                                        },
                                                    }}
                                                >
                                                    View PDF
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleOpenVideo}
                                                    sx={{
                                                        minWidth: 140,
                                                        fontWeight: 600,
                                                        boxShadow: 'none',
                                                        flex: '1 1 auto',
                                                        borderColor: '#CB4422',
                                                        color: '#CB4422',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                            borderColor: '#CB4422',
                                                        },
                                                    }}
                                                >
                                                    View Demo Video
                                                </Button>
                                            </Box>

                                        </>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isSmallScreen ? 'stretch' : 'center', minWidth: 140, width: '100%' }}>
                                            <Box sx={{ display: 'flex', gap: 2, flexDirection: isSmallScreen ? 'column' : 'row' }}>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    disabled
                                                    sx={{
                                                        minWidth: 140,
                                                        fontWeight: 600,
                                                        backgroundColor: '#f5f5f5',
                                                        color: '#bdbdbd',
                                                        boxShadow: 'none',
                                                        width: isSmallScreen ? "100%" : "auto",
                                                        borderColor: '#bdbdbd',
                                                        '&:hover': { backgroundColor: '#f5f5f5', boxShadow: 'none', borderColor: '#bdbdbd' },
                                                    }}
                                                >
                                                    Add to Cart
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => window.open(apiBook.pdfUrl || '/sample.pdf', '_blank')}
                                                    sx={{
                                                        minWidth: 140,
                                                        fontWeight: 600,
                                                        boxShadow: 'none',
                                                        borderColor: '#CB4422',
                                                        color: '#CB4422',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                            borderColor: '#CB4422',
                                                        },
                                                    }}
                                                >
                                                    View PDF
                                                </Button>
                                            </Box>
                                            <Typography variant="body2" sx={{ color: '#dc3d22', mt: 0.5, fontWeight: 600, opacity: 0.7, textAlign: 'center' }}>
                                                Out of Stock
                                            </Typography>
                                        </Box>

                                    ))}
                                    
                                </Box>
                                   

                                    {/* Video Modal */}
                                    <Modal
                                        open={videoModalOpen}
                                        onClose={handleCloseVideo}
                                        aria-labelledby="video-modal-title"
                                        aria-describedby="video-modal-description"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            outline: 'none',
                                            p: isSmallScreen ? 2 : 0,
                                            '& .MuiBackdrop-root': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.9)'
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            width: '100%',
                                            maxWidth: { xs: '100%', sm: '90%', md: '80%', lg: '70%' },
                                            position: 'relative',
                                            aspectRatio: '16/9',
                                            outline: 'none',
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            bgcolor: 'black',
                                            mx: 'auto',
                                            '& iframe': {
                                                width: '100%',
                                                height: '100%',
                                                border: 'none',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0
                                            }
                                        }}>
                                            <IconButton
                                                onClick={handleCloseVideo}
                                                aria-label="close video"
                                                sx={{
                                                    position: 'absolute',
                                                    right: { xs: 10, sm: 20, md: 30 },
                                                    top: { xs: 10, sm: 20, md: 30 },
                                                    color: 'white',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    width: { xs: 36, sm: 36 },
                                                    height: { xs: 36, sm: 36 },
                                                    zIndex: 2,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.2)'
                                                    },
                                                    '& svg': {
                                                        fontSize: { xs: '1.5rem', sm: '1.75rem' }
                                                    }
                                                }}
                                            >
                                                <span style={{ fontSize: '1.5rem' }}>✕</span>
                                            </IconButton>
                                            {apiBook.previewVideoUrl && (() => {
                                                const videoId = getYouTubeId(apiBook.previewVideoUrl);
                                                return videoId ? (
                                                    <YouTube
                                                    videoId={videoId}
                                                    opts={{
                                                      playerVars: {
                                                        autoplay: 1,
                                                        controls: 1,
                                                        rel: 0,
                                                        showinfo: 0,
                                                        modestbranding: 1,
                                                        playsinline: 1,
                                                        loop: 1,
                                                        playlist: videoId
                                                      },
                                                    }}
                                                    style={{
                                                      width: '100%',
                                                      height: '100%',
                                                      placeSelf: 'center',
                                                      overflow: 'hidden'
                                                    }}
                                                  />
                                                  
                                                ) : null;
                                            })()}
                                        </Box>
                                    </Modal>
                                </Box>
                         
                            <Box sx={{ mt: 2, mb: 1 }}>
                                
                                <Divider />
                            </Box>
                            {/* description */}
                            <Box mt={2}>
                                <Typography variant="h4" fontWeight="bold" gutterBottom>About the Book:</Typography>
                                <Typography variant="h5" sx={{ color: "#bcbcbc" }} paragraph>{apiBook.aboutTheBook}</Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Related Books Section - UNCHANGED */}
                    {!hidePurchaseAndRelated && relatedBooks && relatedBooks.length > 0 && (
                        <Box sx={{ mt: 4, mb: 1 }}>
                            <Divider />
                        </Box>
                    )}
                    {!hidePurchaseAndRelated && relatedBooks && relatedBooks.length > 0 && (
                        <Box sx={{ mb: 3, mt: 4 }}>
                            <Typography variant="h3" sx={{
                                fontWeight: 600, color: '#CB4422', borderLeft: '4px solid #000000', paddingLeft: 1,
                            }} >
                                Related Books
                            </Typography>
                        </Box>
                    )}
                    {!hidePurchaseAndRelated && relatedBooks && relatedBooks.length > 0 && (
                        <Box
                            sx={{
                                overflowX: 'auto',
                                width: '100%',
                                pb: 1,
                                '&::-webkit-scrollbar': { display: 'none' },
                                '-ms-overflow-style': 'none',
                                'scrollbar-width': 'none',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    minWidth: 0,
                                }}
                            >
                                {relatedBooks.slice(0, 6).filter(book => book.isActive).map((book) => (
                                    <Card
                                        key={book.id}
                                        disableRipple
                                        disableFocusRipple
                                        disablePadding
                                        sx={{
                                            width: { xs: 250, md: 210 },
                                            maxWidth: 210,
                                            minHeight: 200,
                                            position: 'relative',
                                            boxShadow: 'none',
                                            border: '1px solid lightgrey',
                                            flexShrink: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {book.discountOffer > 0 && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8, left: 0,
                                                    bgcolor: 'black', color: 'white',
                                                    px: 1, py: 0.5, fontSize: '12px', zIndex: 2, borderRadius: 0.5,
                                                }}
                                            >
                                                {book.discountOffer}% off
                                            </Box>
                                        )}
                                        {/* Shopping Cart icon */}
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddToCart({ bookId: book.id });
                                            }}
                                            sx={{
                                                position: 'absolute', 
                                                top: 8, 
                                                right: 8, 
                                                zIndex: 2, 
                                                bgcolor: '#FFB300', 
                                                color: '#000', 
                                                '&:hover': {
                                                    bgcolor: '#FFB300',
                                                },
                                            }}
                                        >
                                            <i className="fa-solid fa-cart-shopping" style={{ fontSize: '14px', color: 'black' }}></i>
                                        </IconButton>
                                        {/* Book Image */}
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            onClick={() => navigate(`/book-overview/${book.id}`)}
                                            image={book?.bookImages?.[0]?.imageUrl}
                                            alt={book.title}
                                            sx={{ 
                                                objectFit: 'cover', 
                                                width: '100%', 
                                                cursor: 'pointer',
                                                aspectRatio: '2/3'
                                            }}
                                        />
                                        <CardContent disablePadding sx={{ textAlign: 'center', p: 1, width: '100%' }}>
                                            <Typography variant="body1" fontWeight="bold" noWrap sx={{ my: 0, fontSize: '14px' }}>
                                                {book.title}
                                            </Typography>
                                            {/* Yearly & Author */}
                                            <Typography fontSize={10} variant="caption" color="text.secondary">
                                                {book.publishingYear && `Yearly - ${book.publishingYear}, `}by {book.authorName || 'Unknown Author'}
                                            </Typography>
                                            
                                            {/* Price and Buy Now */}
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'space-between', 
                                                mt: 1, 
                                                width: '100%',
                                                px: 1
                                            }}>
                                                <Typography variant="h6" color="#000" sx={{ fontSize: '14px' }}>
                                                    ₹{book.price?.toFixed(2) || '0.00'}
                                                </Typography>
                                                <Button 
                                                    variant="contained" 
                                                    size="small" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleBuyNow(book);
                                                    }} 
                                                    sx={{ 
                                                        textTransform: 'none', 
                                                        fontSize: '10px', 
                                                        borderRadius: '2px', 
                                                        px: 2,
                                                        backgroundColor: '#CB4422',
                                                        '&:hover': {
                                                            backgroundColor: '#a8371c',
                                                        }
                                                    }}
                                                >
                                                    Buy Now
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Container>
        </>
    );
};


export default BookOverviewResults;
    